'use client';

import { useState } from 'react';

import { Button } from '@kit/ui/button';

import { DancerSection } from './dancer-section';

interface Props {
  bio: string;
  /** Dancer display name — renders the §12 "About {name}" heading. */
  name?: string;
}

/** Soft cap per Philippine 2026-05-12 brief — long bios show first 300
 * chars + a Read more toggle. The schema still allows 2000 (legacy
 * bios stay intact); the truncation lives in the public render only. */
const BIO_PREVIEW_CHARS = 300;

/**
 * Bio — single Sentient drop-cap on the first paragraph, generous
 * leading, max 70ch for comfortable line length. Long bios (>300 chars)
 * collapse to a preview with a Read more toggle.
 */
export function DancerBio({ bio, name }: Props) {
  const trimmed = bio.trim();
  const isLong = trimmed.length > BIO_PREVIEW_CHARS;
  const [expanded, setExpanded] = useState(false);

  const visible =
    !isLong || expanded ? trimmed : softTruncate(trimmed, BIO_PREVIEW_CHARS);

  const paragraphs = visible
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return null;

  return (
    <DancerSection eyebrow="Bio" title={name ? `About ${name}` : 'About'}>
      <div className="text-foreground/85 max-w-[70ch] space-y-6 text-base leading-[1.75] sm:text-lg">
        {paragraphs.map((p, i) => (
          <p key={i} className={i === 0 ? 'first-letter-drop' : undefined}>
            {p}
            {/* Trailing ellipsis lives on the last paragraph only, and
                only while collapsed. */}
            {isLong && !expanded && i === paragraphs.length - 1 ? '…' : null}
          </p>
        ))}
      </div>

      {isLong && (
        <Button
          type="button"
          variant="link"
          onClick={() => setExpanded((v) => !v)}
          className="text-muted-foreground hover:text-foreground mt-6 inline-flex h-auto p-0 font-mono text-[11px] tracking-[0.22em] uppercase no-underline transition-colors hover:no-underline"
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Read more'}
        </Button>
      )}
    </DancerSection>
  );
}

/** Cut on a word boundary near `max` chars. Avoids breaking mid-word
 * and keeps trailing punctuation tidy. */
function softTruncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const window = text.slice(0, max);
  const lastSpace = window.lastIndexOf(' ');
  // Fall back to a hard cut if there's no whitespace in the window.
  const cut = lastSpace > max * 0.6 ? lastSpace : max;
  return text.slice(0, cut).replace(/[\s.,;:!?-]+$/, '');
}
